import {
  Box,
  Button,
  Heading,
  Icon,
  Link as ChakraLink,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  OrderedList,
  SimpleGrid,
  Text,
  UnorderedList,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import type { FC } from 'react';
import { memo } from 'react';
import {
  BiWind,
  BiTrash,
  BiLogOut,
  BiTaskX,
  BiKey,
  BiLogIn,
  BiHomeSmile,
  BiPaintRoll,
  BiTv,
} from 'react-icons/bi';
import { Link } from 'react-router-dom';
import '../App.scss';
import useGaTracker from '../components/PageTracking';
import useSeo from '../components/useSeo';

// Information card component for better organization
interface InfoCardProps {
  title: string;
  description: string;
  icon: any;
  onClick: () => void;
}

const InfoCard: FC<InfoCardProps> = memo(({ title, description, icon, onClick }) => (
  <button className="info-card fade-in" onClick={onClick} type="button">
    <span className="info-card__icon">
      <Icon as={icon} />
    </span>
    <h3>{title}</h3>
    <p>{description}</p>
    <span className="info-card__link">
      Læs mere <span>→</span>
    </span>
  </button>
));

InfoCard.displayName = 'InfoCard';

const LejerInfo: FC = memo(() => {
  // All modal disclosures
  const klimaModal = useDisclosure();
  const skrealdModal = useDisclosure();
  const fraflytningModal = useDisclosure();
  const opsigelseModal = useDisclosure();
  const keysModal = useDisclosure();
  const indflytningModal = useDisclosure();
  const husordenModal = useDisclosure();
  const vedligeholdelseModal = useDisclosure();
  const antenneModal = useDisclosure();

  useGaTracker();
  useSeo({
    title: 'Lejerinformation | Toft Ejendomme',
    description:
      'Praktisk lejerinformation om indeklima, affald, indflytning, fraflytning, nøgler, husorden og vedligeholdelse.',
    path: '/lejerinfo',
  });

  // Bold text component
  const B = (props: any) => (
    <Text as="span" fontWeight="bold">{props.children}</Text>
  );

  return (
    <>
      <section className="page-masthead">
        <div className="section-wrap">
          <div className="page-masthead__grid fade-in">
            <div className="page-masthead__copy">
              <span className="eyebrow">Lejerinformation</span>
              <h1 className="page-title">Praktisk information for lejere</h1>
              <p className="page-lead">
                Her finder du vigtig information og retningslinjer om alt fra indeklima og affald til
                indflytning, opsigelse og vedligeholdelse.
              </p>
              <p className="page-lead">
                Klik på et kort for at åbne de detaljerede retningslinjer i samme vindue.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="page-flow">
        <div className="section-wrap">
          <SimpleGrid className="info-grid" columns={[1, 2, 3]} spacing={6} w="100%">
            <InfoCard
              title="Indeklima"
              description="10 gode råd til et godt indeklima"
              icon={BiWind}
              onClick={klimaModal.onOpen}
            />
            <InfoCard
              title="Affald, pap og storskrald"
              description="Sådan skiller du dig af med dit affald"
              icon={BiTrash}
              onClick={skrealdModal.onOpen}
            />
            <InfoCard
              title="Fraflytning"
              description="Flyttesyn & rengøring"
              icon={BiLogOut}
              onClick={fraflytningModal.onOpen}
            />
            <InfoCard
              title="Opsigelse af lejemål"
              description="Frister og procedurer"
              icon={BiTaskX}
              onClick={opsigelseModal.onOpen}
            />
            <InfoCard
              title="Nøgler"
              description="Udlevering og aflevering af nøgler"
              icon={BiKey}
              onClick={keysModal.onOpen}
            />
            <InfoCard
              title="Indflytning"
              description="Vand/varme, el og fejl & mangler"
              icon={BiLogIn}
              onClick={indflytningModal.onOpen}
            />
            <InfoCard
              title="Husorden"
              description="Gældende husorden for alle vores boliger"
              icon={BiHomeSmile}
              onClick={husordenModal.onOpen}
            />
            <InfoCard
              title="Generel Vedligeholdelse"
              description="Lejers forpligtelser til vedligeholdelse"
              icon={BiPaintRoll}
              onClick={vedligeholdelseModal.onOpen}
            />
            <InfoCard
              title="Antenneforhold"
              description="Information om TV-modtagning i vores lejligheder"
              icon={BiTv}
              onClick={antenneModal.onOpen}
            />
          </SimpleGrid>
        </div>
      </section>

      {/* All Modals */}
      <Modal isOpen={klimaModal.isOpen} onClose={klimaModal.onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Heading fontSize={['lg', 'xl']} fontWeight="bold">
              10 gode råd om opvarmning og udluftning
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <OrderedList spacing={4}>
              <ListItem>
                Hold normalt alt lukket i fyringssæsonen og undgå træk. Når du
                ønsker at lufte ud, gøres det bedst ved at lave kortvarige
                kraftige udluftninger af 5-10 minutters varighed. Skru ned for
                dine radiatorer, mens du lufter ud. Dug indvendig på
                termovinduer er tegn på, at der skal luftes mere ud.
              </ListItem>
              <ListItem>
                Undgå en høj luftfugtighed i lejligheden. En fugtmættet luft vil
                føles koldere end en tør luft ved samme temperatur. Og fugt kan
                skade boligen. Fugtig luft er dyrere at opvarme end tør luft.
              </ListItem>
              <ListItem>
                Tilstop ikke ventilationsåbninger i badeværelse og køkken.
              </ListItem>
              <ListItem>
                Lad være med at tørre tøj på radiatorerne. Det giver for høj
                fugt i din bolig med risiko for fugtskader.
              </ListItem>
              <ListItem>
                Har du flere radiatorer i samme rum, skal du stille alle på
                samme temperatur.
              </ListItem>
              <ListItem>
                Hold dørene lukket, hvis du ønsker lavere temperaturer i nogle
                rum. For eksempel i soveværelse og køkken.
              </ListItem>
              <ListItem>
                Det koster dig minimum 3-5 % mere i energiforbrug, hvis du hæver
                varmen med 1 grad.
              </ListItem>
              <ListItem>
                Undgå at tildække radiatorer og termostatventiler med gardiner
                og møbler. En varmemåler på en tildækket radiator vil registrere
                en meget høj varmeafgivelse uden at den kommer rummet til gode.
              </ListItem>
              <ListItem>
                Stil ikke møbler helt op af kolde ydervægge, da dette kan give
                mug på vægge.
              </ListItem>
              <ListItem>
                Tager du væk fra din lejlighed i længere tid, kan du skrue lidt
                ned for varmen. Du må dog ikke lukke helt, da du er forpligtet
                til at vedligeholde din lejlighed ved at opvarme den, så der
                ikke sker fugt- eller frostskader
              </ListItem>
            </OrderedList>
          </ModalBody>
          <ModalFooter>
            <Link to="indeklima.pdf" target="_blank" download>
              <Button colorScheme="blue">Download som PDF</Button>
            </Link>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={skrealdModal.isOpen} onClose={skrealdModal.onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Heading fontSize={['lg', 'xl']} fontWeight="bold">
              Affald, pap og storskrald
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Der henstilles til, at de opstillede skraldespande/containere ikke
              anvendes til bortskaffelse af pap og andet storskrald.
            </Text>
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>

      <Modal isOpen={fraflytningModal.isOpen} onClose={fraflytningModal.onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Heading fontSize={['lg', 'xl']} fontWeight="bold">
              Fraflytning
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontWeight="bold" mb={2}>Elaflæsning</Text>
                <Text>
                  I forbindelse med fraflytning aflæser den fraflyttende lejer selv
                  elmåler. Aflæsningen indtelefoneres til Energi Midt på telefon 70 15
                  15 60. Elregningen sendes direkte til lejeren, da der ikke betales
                  for dette til udlejer via huslejen.
                </Text>
              </Box>

              <Box>
                <Text fontWeight="bold" mb={2}>Flyttesyn</Text>
                <Text mb={2}>
                  Ifølge lejekontrakten udarbejder lejer og udlejer sammen en
                  fraflytningsrapport. De nærmere vilkår vedr. fraflytning findes i lejekontrakten.
                </Text>
                <Text mb={2}>
                  Lejer skal aftale flyttesyn med udlejer senest <B>14 dage</B> før
                  fraflytning.
                </Text>
                <Text mb={2}>
                  Flyttesynet finder sted, når lejemålet er fuldstændigt tømt og
                  hovedrengjort med nypudsede vinduer ind- og udvendigt, herunder
                  udskiftning af emhættefilter samt vask af trappe/r efter
                  fraflytning. (uagtet der betales for trappevask).
                </Text>
                <Text mb={4}>
                  Samtlige nøgler til lejemålet skal afleveres ved fraflytningssynet.
                </Text>

                <Text fontWeight="bold" mb={2}>
                  Ved syn opleves desværre ofte følgende mangler omkring lejers
                  rengøring:
                </Text>
                <UnorderedList spacing={2}>
                  <ListItem>Eftertørring af skabslåger efter rengøring.</ListItem>
                  <ListItem>Rengøring af emhætte indvendig.</ListItem>
                  <ListItem>Emhættefilter er ikke udskiftet.</ListItem>
                  <ListItem>
                    Rengøring af rør, radiator, vindueskarme og stikkontakter.
                  </ListItem>
                  <ListItem>
                    Rengøring og pudsning af vinduer ind- og udvendigt.
                  </ListItem>
                  <ListItem>Rengøring af trapper efter flytning.</ListItem>
                  <ListItem>
                    Ovn er ikke renset og rengjort, herunder bageplader og riste.
                  </ListItem>
                  <ListItem>Komfurets udvendige sider er ikke rengjorte.</ListItem>
                  <ListItem>
                    Køle/frys: Skuffer, hylder og riste er ikke intakte/rengjorte.
                  </ListItem>
                  <ListItem>
                    Blandingsbatterier, brusehoved, fliser på væg og gulv især i
                    badeværelse og især ved afløbsforhold er ikke
                    afkalkede/rengjorte.
                  </ListItem>
                  <ListItem>
                    Loftsrosetter til el-udtag er ikke monteret efter nedtagning af
                    lamper.
                  </ListItem>
                  <ListItem>
                    Badeværelsesforhæng (hvis der er et sådant) er ikke blevet
                    vasket/skiftet.
                  </ListItem>
                  <ListItem>
                    Pærer i ovn, køleskab, emhætte og faste lampe virker ikke. –
                    Husk at skifte pærer.
                  </ListItem>
                </UnorderedList>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>

      <Modal isOpen={opsigelseModal.isOpen} onClose={opsigelseModal.onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Heading fontSize={['lg', 'xl']} fontWeight="bold">
              Opsigelse af lejemål
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <UnorderedList spacing={3}>
                <ListItem>
                  Opsigelsen skal være mailet til Toft Ejendomme på{' '}
                  <ChakraLink href="mailto:info@toftejendomme.dk" color="blue.600">
                    info@toftejendomme.dk
                  </ChakraLink>{' '}
                  senest sidste hverdag i måneden
                </ListItem>
                <ListItem>
                  Opsigelsen er først gyldig, når den er underskrevet af samtlige
                  lejere i henhold til lejekontrakten
                </ListItem>
                <ListItem>
                  Ønskes lejemålet genudlejet før opsigelsesfristens udløb oplyses
                  dette i opsigelsen (under "Lejemålet ønskes genudlejet den ...")
                </ListItem>
              </UnorderedList>
              <Text>
                Når du har opsagt dit lejemål, vil du modtage en bekræftelse på
                opsigelsen.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>

      <Modal isOpen={keysModal.isOpen} onClose={keysModal.onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Heading fontSize={['lg', 'xl']} fontWeight="bold">
              Nøgler
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Nøgler til lejemålet udleveres i forbindelse med indflytning.
              Hvis det ikke er en systemnøgle kan lejer selv lave kopier heraf.
              Er det en systemnøgle, skal Toft Ejendomme fremskaffe ekstra
              nøgler, som betales af lejeren. Vi har desværre ikke mulighed for at låse op i tilfælde af tabt
              nøgle, men henviser i stedet til låsesmed. Når lejemålet
              fraflyttes afleveres det samme antal nøgler, som blev udleveret ved
              indflytning samt evt. ekstra kopier.
            </Text>
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>

      <Modal isOpen={indflytningModal.isOpen} onClose={indflytningModal.onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Heading fontSize={['lg', 'xl']} fontWeight="bold">
              Indflytning
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Ved indflytning skal lejeren, hvis der er fejl eller mangler
              ved lejligheden, inden 14 dage skriftligt udarbejde en liste, der
              skal fremsendes til udlejer. Ved indflytning aflæses el og lejer skal herefter selv tage
              kontakt til Energi Midt på 70 15 15 60 og afgive de aflæste tal.
              Udlejer bestiller aflæsning af varmemålere/vandmålere, og lejeren
              vil blive kontaktet direkte vedr. tidspunkt for dette.
            </Text>
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>

      <Modal isOpen={husordenModal.isOpen} onClose={husordenModal.onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Heading fontSize={['lg', 'xl']} fontWeight="bold">
              Husorden
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <OrderedList spacing={4}>
              <ListItem>
                <B>Generelt</B>
                <Text>
                  Denne husorden er gældende som vilkår til den indgående
                  lejekontrakt
                </Text>
              </ListItem>
              <ListItem>
                <B>Påbud, Ansvar for gæster m.v.</B>
                <Text>
                  Lejeren er udover de skrevne regler i HUSORDENEN endvidere
                  forpligtet til at efterkomme og overholde de mundtlige eller
                  skriftlige påbud, der måtte blive meddelt af udlejeren eller
                  dennes stedfortræder, herunder vicevært, hvilket også gælder
                  de personer, som lejeren er ansvarlig for.
                </Text>
              </ListItem>
              <ListItem>
                <B>Ændring af Husorden</B>
                <Text>
                  Reglerne i denne husorden kan efterfølgende løbende ændres
                  eller revideres af udlejeren, såfremt der findes behov herfor.
                </Text>
              </ListItem>
              <ListItem>
                <B>Vaskerum m.v.</B>
                <Text>
                  De i fællesvaskeriet, hvis et sådan findes, opsatte regler for
                  brug og renholdelse udgør også en del af de gældende
                  husordensregler. Tøjtørring o.lign. må KUN ske i tørrerummet
                  eller udendørs og kun på de af udlejer/vicevært anviste
                  steder. Det er forbudt at tørre tøj i lejligheden, da fugten herfra kan forringe
                  eller ødelægge tapet og træværk.
                </Text>
              </ListItem>
              <ListItem>
                <B>Fællesarealer, pulterrum, m.v.</B>
                <Text>
                  Lejeren har kun ret til at disponere over fællesrum og
                  fællesarealer, såvel indendørs som udendørs, efter anvisning
                  og med respekt af de øvrige lejeres tilsvarende brugsret. Det
                  er ikke tilladt at henstille indbo, andre effekter, affald
                  el.lign. på trapper eller øvrige fællesarealer.
                </Text>
              </ListItem>
              <ListItem>
                <B>Affald</B>
                <Text>
                  Lejers affald må kun henkastes i de hertil anviste beholdere,
                  og dette skal ske i lukkede plastposer og må ikke indeholde
                  skarpe og spidse genstande.
                </Text>
              </ListItem>
              <ListItem>
                <B>Støj, Fester m.v.</B>
                <Text>
                  Der er mere lydt i en beboelsesejendom, end de fleste er klar
                  over! - Derfor skal der generelt udvises hensyn overfor andre
                  lejere. Hvis der holdes fest i lejligheden, skal
                  alle omkringboende lejere orienteres 1 uge i forvejen og efter
                  kl. 23 skal musikstøj dæmpes til et absolut minimum.
                </Text>
              </ListItem>
              <ListItem>
                <B>Husdyr</B>
                <Text>
                  Det er IKKE tillagt at holde husdyr i vores ejendomme.
                </Text>
              </ListItem>
            </OrderedList>
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>

      <Modal isOpen={vedligeholdelseModal.isOpen} onClose={vedligeholdelseModal.onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Heading fontSize={['lg', 'xl']} fontWeight="bold">
              Generel Vedligeholdelse
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Lejer forpligtiger sig til, i henhold til lejekontrakten, at stå for
              den indvendige vedligeholdelse, herunder bl.a. fornyelse af tapet og
              maling, så lejligheden til enhver tid fremstår i god og vedligeholdt
              stand. Udlejers forsikring dækker ikke glas og kumme i
              de enkelte lejligheder.
            </Text>
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>

      <Modal isOpen={antenneModal.isOpen} onClose={antenneModal.onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Heading fontSize={['lg', 'xl']} fontWeight="bold">
              Antenne Forhold
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              I de fleste af vore ejendomme, er der lagt kabel ind, så det er
              muligt at købe minimumspakken i disse ejendomme.
            </Text>
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>
    </>
  );
});

LejerInfo.displayName = 'LejerInfo';

export default LejerInfo;
